import { useEffect, useState } from 'react'

import api from '@disco/api/server'
import captureError from '@disco/utils/captureError'

/**
 * Loads the Chargebee pricing page url for the business's subscription, for CTAs that link to it.
 * @param redirect - DISCO path Chargebee sends the user back to after checkout
 * @param enabled - Skips the request when nobody on the page can use the link
 * @returns the url once loaded, and whether it failed to load
 */
export default function useChargebeePricingPage(
  redirect: string,
  enabled = true,
): { url?: string; hasFailed: boolean } {
  const [url, setUrl] = useState<string>()
  const [hasFailed, setHasFailed] = useState(false)

  useEffect(() => {
    if (!enabled) return

    let isCurrent = true
    api
      .chargebeeSubscriberPricingForm({ redirect })
      .then(response => {
        if (!response?.url) {
          throw new Error('Chargebee pricing page returned no url')
        }
        if (isCurrent) setUrl(response.url)
      })
      .catch(error => {
        if (!isCurrent) return
        setHasFailed(true)
        captureError(error, {
          moduleName: 'useChargebeePricingPage',
          description: 'Failed to load the Chargebee pricing page url',
        })
      })

    return () => {
      isCurrent = false
    }
  }, [redirect, enabled])

  return { url, hasFailed }
}
